import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
    Form,
    Spinner,
    Button,
    Modal,
    Alert,
    Tab,
    Tabs,
} from "react-bootstrap";
import { FiSave } from "react-icons/fi";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@features/auth/store/authStore";
import { useActivityEntry } from "@features/report/activity-entry/hooks/useActivityEntry";
import { useActivityEntryAction } from "@features/report/activity-entry/hooks/useActivityEntryAction";
import {
    areActivityEntryHeaderContextsEqual,
    type ActivityEntryHeaderContext,
} from "@features/report/activity-entry/utils/activityEntryHeaderContext";
import { ActivityEntryFormGroup } from "@features/report/activity-entry/types/activityEntry";
import ActivityEntrySubgroupSummary from "@features/report/activity-entry/components/ActivityEntrySubgroupSummary";
import {
    SUMMARY_TAB_EVENT_KEY,
    SUMMARY_TAB_LABEL,
} from "@features/report/activity-entry/constants/activityEntrySummary";
import {
    buildFormValuesFromGroups,
    collectCalculatedValueKeys,
} from "@features/report/activity-entry/utils/activityEntryFormValues";
import NoResult from "@core/components/NoResult";
import { fetchChurchById } from "@features/church/api/churchApi";
import { fetchDistrictById } from "@features/district/api/districtApi";
import { fetchAssignedActivityAsync } from "@features/admin/activity/services/activitySvc";
import { fetchAssignedKpiAsync } from "@features/admin/kpi/services/kpiSvc";
import { useReportTags } from "@features/report/tag/hooks/useReportTags";
import { useReportTemplates } from "@features/report/template/hooks/useReportTemplates";
import { fetchTemplatePreviewAsync } from "@features/report/template/services/reportTemplateSvc";
import { useDepartments } from "@features/department/hooks/useDepartments";
import { useErrorModalStore } from "@infrastructure/api/store/errorModalStore";
import { useApiError } from "@infrastructure/api/hooks/useApiError";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { ErrorResponse } from "@core/types/errorResponse";
import {
    canEditActivitySubmission,
    canRequestActivityReview,
    getActivityEditabilityText,
    getReportTagStatusLabel,
    getRequestReviewCopy,
    getWorkflowStatusLabel,
    getWorkflowStatusVariant,
    isTagOpen,
    normalizeReportWorkflowStatus,
} from "@core/reports/utils/reportWorkflow";
import { useReportActionPermissions } from "@core/reports/hooks/useReportActionPermissions";
import { buildReportViewSearch } from "@core/reports/constants/reportSpaQueryParams";
import { getApiScopeFromValue } from "@features/invitations/utils/getScopeFromParam";
import { getScopedActivityEntryCode } from "@features/entity/types/actionCodes";
import { fetchDepartmentReportsQueryAsync, type DepartmentReportListItem } from "@core/reports/api/fetchDepartmentReportsQuery";
import { requestActivityEntryReviewAsync, requestDistrictActivityEntryReviewAsync, requestNationActivityEntryReviewAsync } from "@features/report/activity-entry/api/activityEntryApi";
import {
    ActivityContext,
    getActivityAccordionKey,
    getActivityKpiContexts,
    getAllActivityContexts,
    getAllKpiContexts,
    getGroupActivityContexts,
    getKpiValueKey,
    sortSubGroups,
} from "@features/report/activity-entry/utils/activityEntryGroupHelpers";
import {
    ACTIVITY_ENTRY_PREFERENCES_UPDATED_EVENT,
    getActivityEntryAutoSaveEnabled,
} from "@features/report/activity-entry/utils/activityEntryPreferences";
import { isKpiRequiredForReportTag } from "@features/report/activity-entry/utils/kpiRequirement";
import { useActivityEntryForm } from "@features/report/activity-entry/hooks/useActivityEntryForm";
import { useActivityEntryDrafting } from "@features/report/activity-entry/hooks/useActivityEntryDrafting";
import ActivityFormAccordion from "@features/report/activity-entry/components/ActivityFormAccordion";
import { mapTemplatePreviewToFormGroups } from "@features/report/template/utils/mapTemplatePreviewToFormGroups";
import ActivityFormSelector from "@features/report/activity-entry/components/ActivityFormSelector";
import { ActivityEntryPrefillControls } from "@features/report/activity-entry/components/ActivityEntryPrefillControls";
import {
    buildActivityEntrySubmitSearch,
    parseActivityEntrySubmitSearch,
} from "@features/report/activity-entry/constants/activityEntryQueryParams";

function subgroupTabKey(reportSubGroupId: number | null | undefined, fallback: string): string {
    return reportSubGroupId != null ? String(reportSubGroupId) : fallback;
}

type AssignedTemplateActivityRow = {
    report_template_id: number;
    report_template_name: string;
    department_id: number;
    department_name: string;
    activity_id: number;
    activity_name: string;
    activity_description?: string | null;
    nation_id?: number;
    list_order?: number | null;
};

type AssignedActivityKpiRow = {
    kpi_id: number;
    kpi_name: string;
    kpi_description?: string | null;
    kpi_is_active?: boolean;
    data_type_id?: number | null;
    data_type_name?: string | null;
    unit_id?: number | null;
    unit_name?: string | null;
    abbreviation?: string | null;
    display_position?: string | null;
    list_order?: number | null;
    is_required?: boolean;
    required_at?: string | null;
};

function extractArrayPayload<T>(payload: unknown): T[] {
    if (Array.isArray(payload)) return payload as T[];
    if (payload && typeof payload === "object" && Array.isArray((payload as { data?: unknown }).data)) {
        return (payload as { data: T[] }).data;
    }
    return [];
}

function mapAssignedTemplateQueryToFormGroups(
    activities: AssignedTemplateActivityRow[],
    kpisByActivityId: Record<number, AssignedActivityKpiRow[]>
): ActivityEntryFormGroup[] {
    if (activities.length === 0) return [];

    const first = activities[0];

    return [{
        report_template: {
            report_template_id: first.report_template_id,
            report_template_name: first.report_template_name,
            department_id: first.department_id,
            department_name: first.department_name,
            activity_report_id: null,
            activity_report_status_id: null,
            activity_report_status_name: null,
            process_results: null,
        },
        activities: activities.map((activity) => ({
            activity_id: activity.activity_id,
            activity_name: activity.activity_name,
            activity_is_active: true,
            activity_list_order: activity.list_order ?? 0,
            kpis: (kpisByActivityId[activity.activity_id] ?? []).map((kpi) => ({
                kpi_id: kpi.kpi_id,
                kpi_name: kpi.kpi_name,
                kpi_is_active: kpi.kpi_is_active ?? true,
                kpi_list_order: kpi.list_order ?? 0,
                kpi_is_required: kpi.is_required ?? false,
                kpi_required_at: kpi.required_at ?? null,
                data_type: {
                    data_type_id: kpi.data_type_id ?? 0,
                    data_type_name: kpi.data_type_name ?? "",
                },
                unit: kpi.unit_id
                    ? {
                        unit_id: kpi.unit_id,
                        unit_name: kpi.unit_name ?? "",
                        unit_abbreviation: kpi.abbreviation ?? null,
                        unit_display_position: kpi.display_position ?? "suffix",
                    }
                    : null,
                submission: null,
            })),
        })),
    }];
}

function isFilled(v: string | undefined) {
    return !!(v ?? "").toString().trim();
}

interface ActivityEntryFormProps {
    scope?: { type: "church" | "district" | "nation"; id: number } | null;
    /** Called when form data is loaded so the page can show template/department as the header */
    onHeaderContextChange?: (ctx: ActivityEntryHeaderContext | null) => void;
}

function ActivityEntryForm({ scope, onHeaderContextChange }: ActivityEntryFormProps) {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const lastNotifiedHeaderRef = useRef<ActivityEntryHeaderContext | null | undefined>(undefined);
    const basePath = location.pathname;

    // Entity ID comes from the scoped route; missing scope stays idle (selector, no church default).
    const entityScopeType = scope?.type;
    const entityId = scope?.id && scope.id > 0 ? scope.id : 0;
    const churchId = entityScopeType === "church" ? entityId : 0;
    const { reportTemplateId, reportTagId, departmentId: departmentIdParam } =
        parseActivityEntrySubmitSearch(searchParams);

    const [page, setPage] = useState(1);
    const [openKeys, setOpenKeys] = useState<string[]>([]);
    const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [parentDepartmentReport, setParentDepartmentReport] = useState<DepartmentReportListItem | null>(null);
    const [parentDepartmentReportLoading, setParentDepartmentReportLoading] = useState(false);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [reviewReason, setReviewReason] = useState("");
    const [reviewTouched, setReviewTouched] = useState(false);
    const [reviewError, setReviewError] = useState<string | null>(null);
    const [showDepartmentReportNotice, setShowDepartmentReportNotice] = useState(false);
    const [previewGroups, setPreviewGroups] = useState<ActivityEntryFormGroup[]>([]);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [activeSubGroupTabs, setActiveSubGroupTabs] = useState<Record<string, string>>({});
    const [autoSaveEnabled, setAutoSaveEnabled] = useState<boolean>(() =>
        getActivityEntryAutoSaveEnabled()
    );
    const [calculatedWarningsByKpiId, setCalculatedWarningsByKpiId] = useState<
        Record<number, string>
    >({});
    const [gridModalOpen, setGridModalOpen] = useState(false);


    const { busy, doSaveDraft, doSubmit } = useActivityEntryAction(entityScopeType);
    const showValidation = useErrorModalStore((s) => s.showValidation);
    const showError = useApiError();

    const [selector, setSelector] = useState({
        department_id: departmentIdParam > 0 ? departmentIdParam : 0,
        report_template_id: reportTemplateId > 0 ? reportTemplateId : 0,
        report_tag_id: reportTagId > 0 ? reportTagId : 0,
    });

    const enabled = Boolean(entityScopeType) && entityId > 0 && reportTemplateId > 0 && reportTagId > 0;

    const showInactive = false;
    const reviewReasonTrimmed = reviewReason.trim();
    const reviewReasonValid =
        reviewReasonTrimmed.length >= 10 && reviewReasonTrimmed.length <= 1000;

    useEffect(() => setPage(1), [entityId, reportTemplateId, reportTagId]);

    useEffect(() => {
        const syncPreference = () => {
            setAutoSaveEnabled(getActivityEntryAutoSaveEnabled());
        };

        const handleStorageChange = () => {
            syncPreference();
        };

        syncPreference();
        window.addEventListener("storage", handleStorageChange);
        window.addEventListener(
            ACTIVITY_ENTRY_PREFERENCES_UPDATED_EVENT,
            syncPreference
        );

        return () => {
            window.removeEventListener("storage", handleStorageChange);
            window.removeEventListener(
                ACTIVITY_ENTRY_PREFERENCES_UPDATED_EVENT,
                syncPreference
            );
        };
    }, []);

    const { data: entryData, loading, requestFailed, reload } = useActivityEntry({
        scope: entityScopeType,
        entityId,
        reportTemplateId,
        reportTagId,
        page,
    });

    const departmentScope = useMemo(() => {
        if (!scope?.id) return {};
        if (scope.type === 'church') return { churchId: scope.id };
        if (scope.type === 'district') return { districtId: scope.id };
        if (scope.type === 'nation') return { nationId: scope.id };
        return {};
    }, [scope]);
    const activityWriteActionCodes = useMemo(
        () =>
            [
                getScopedActivityEntryCode(entityScopeType ?? "", "save"),
                getScopedActivityEntryCode(entityScopeType ?? "", "submit"),
            ].filter(Boolean),
        [entityScopeType]
    );
    const { departments, loading: departmentsLoading } = useDepartments({
        all: true,
        isActive: true,
        ...departmentScope,
        actionCodes: activityWriteActionCodes,
    });
    const templateChurchId = useMemo(() => {
        if (entityScopeType === "church" && entityId > 0) return entityId;
        return undefined;
    }, [entityScopeType, entityId]);
    const templateScopeParams = useMemo(() => {
        if (scope?.type === "district" && scope.id > 0) return { districtId: scope.id };
        if (scope?.type === "nation" && scope.id > 0) return { nationId: scope.id };
        if (templateChurchId) return { churchId: templateChurchId };
        return {};
    }, [scope, templateChurchId]);
    const { data: templates = [], loading: templatesLoading } = useReportTemplates({
        departmentId: selector.department_id,
        ...templateScopeParams,
        all: true,
        isActive: true,
    });
    const tagParams = useMemo(() => {
        if (entityScopeType === "district" && entityId > 0) return { districtId: entityId };
        if (entityScopeType === "nation" && entityId > 0) return { nationId: entityId };
        if (entityScopeType === "church" && entityId > 0) return { churchId: entityId };
        return {};
    }, [entityScopeType, entityId]);
    const { data: tags = [], loading: tagsLoading } = useReportTags({
        ...tagParams,
        all: true,
        // Selector only offers OPEN periods; opened forms load all so deep-linked
        // PENDING/CLOSED tags still resolve status for gating.
        ...(enabled ? {} : { reportTagStatus: "OPEN" }),
    });

    const openTagsForSelector = useMemo(
        () =>
            tags.filter((tg: { report_tag_status_name?: string | null }) =>
                isTagOpen(tg.report_tag_status_name)
            ),
        [tags]
    );

    const activeDepartmentId = useMemo(() => {
        const templateDepartmentId = entryData?.[0]?.report_template?.department_id ?? 0;
        return templateDepartmentId || selector.department_id || departmentIdParam || 0;
    }, [entryData, selector.department_id, departmentIdParam]);

    const permissionScope = getApiScopeFromValue(entityScopeType ?? "");

    const {
        loading: permissionsLoading,
        canSaveActivity,
        canSubmitActivity,
        canReviewActivity,
    } = useReportActionPermissions({
        scope: permissionScope,
        scopeId: entityId,
        departmentId: activeDepartmentId,
        includeActivityEntry: true,
        enabled: entityId > 0 && activeDepartmentId > 0,
    });

    const resolveNationIdForFallback = useCallback(async (): Promise<number | null> => {
        if (scope?.type === "nation" && scope.id > 0) {
            return scope.id;
        }

        if (scope?.type === "district" && scope.id > 0) {
            const districtResponse = await fetchDistrictById(scope.id);
            if (!districtResponse.isSuccess()) return null;
            return Number(districtResponse.getData()?.nation_id ?? 0) || null;
        }

        if (churchId > 0) {
            const churchResponse = await fetchChurchById(churchId);
            if (!churchResponse.isSuccess()) return null;

            const districtId = Number(churchResponse.getData()?.district_id ?? 0);
            if (districtId <= 0) return null;

            const districtResponse = await fetchDistrictById(districtId);
            if (!districtResponse.isSuccess()) return null;

            return Number(districtResponse.getData()?.nation_id ?? 0) || null;
        }

        return null;
    }, [churchId, scope]);

    const buildFallbackGroupsFromNationQueries = useCallback(async (
        nationId: number
    ): Promise<ActivityEntryFormGroup[]> => {
        const activityResponse = await fetchAssignedActivityAsync(
            nationId,
            activeDepartmentId,
            reportTemplateId,
            { all: true, page: 1, perPage: 500 }
        );

        if (!activityResponse.isSuccess()) {
            return [];
        }

        const activityRows = extractArrayPayload<AssignedTemplateActivityRow>(
            activityResponse.getData()
        );

        if (activityRows.length === 0) {
            return [];
        }

        const kpisByActivityIdEntries = await Promise.all(
            activityRows.map(async (activityRow) => {
                const kpiResponse = await fetchAssignedKpiAsync(
                    nationId,
                    activityRow.activity_id,
                    { all: true, page: 1, perPage: 500 }
                );

                if (!kpiResponse.isSuccess()) {
                    return [activityRow.activity_id, []] as const;
                }

                return [
                    activityRow.activity_id,
                    extractArrayPayload<AssignedActivityKpiRow>(kpiResponse.getData()),
                ] as const;
            })
        );

        const kpisByActivityId = Object.fromEntries(kpisByActivityIdEntries);

        return mapAssignedTemplateQueryToFormGroups(activityRows, kpisByActivityId);
    }, [activeDepartmentId, reportTemplateId]);

    const loadTemplatePreviewFallback = useCallback(async () => {
        if (!enabled || loading || requestFailed) return;

        if (entryData.length > 0) {
            setPreviewGroups([]);
            setPreviewLoading(false);
            return;
        }

        if (activeDepartmentId <= 0 || reportTemplateId <= 0) {
            setPreviewGroups([]);
            setPreviewLoading(false);
            return;
        }

        setPreviewLoading(true);
        try {
            const nationId = await resolveNationIdForFallback();

            if (!nationId) {
                setPreviewGroups([]);
                return;
            }

            const previewResponse = await fetchTemplatePreviewAsync(
                activeDepartmentId,
                reportTemplateId,
                { nationId }
            );

            if (previewResponse.isSuccess()) {
                const previewGroups = mapTemplatePreviewToFormGroups(
                    previewResponse.getData() ?? null
                );

                if (previewGroups.length > 0) {
                    setPreviewGroups(previewGroups);
                    return;
                }
            }

            setPreviewGroups(
                await buildFallbackGroupsFromNationQueries(nationId)
            );
        } catch {
            setPreviewGroups([]);
        } finally {
            setPreviewLoading(false);
        }
    }, [
        activeDepartmentId,
        buildFallbackGroupsFromNationQueries,
        enabled,
        entryData,
        loading,
        requestFailed,
        reportTemplateId,
        resolveNationIdForFallback,
    ]);

    useEffect(() => {
        setPreviewGroups([]);
        setPreviewLoading(false);
    }, [entityId, reportTemplateId, reportTagId]);

    useEffect(() => {
        void loadTemplatePreviewFallback();
    }, [loadTemplatePreviewFallback]);

    const data = useMemo(() => {
        if (entryData.length > 0) return entryData;
        return previewGroups;
    }, [entryData, previewGroups]);

    const allActivityContexts = useMemo(
        () => getAllActivityContexts(data, showInactive),
        [data, showInactive]
    );

    const allKpiContexts = useMemo(
        () => getAllKpiContexts(data, showInactive),
        [data, showInactive]
    );

    const allKpiContextsIncludingInactive = useMemo(
        () => getAllKpiContexts(data, true),
        [data]
    );

    const {
        values,
        handleChange,
        isActivityDirty,
        unsavedCount,
        markSaved,
        applyServerValues,
        applyPrefillValues,
    } = useActivityEntryForm({
        enabled,
        hydrationKey: `${entityScopeType}-${entityId}-${reportTemplateId}-${reportTagId}`,
        allKpiContexts,
        allActivityContexts,
        showInactive,
    });

    const selectedTag = useMemo(() => {
        const listTag = tags.find((tg: any) => Number(tg.report_tag_id) === reportTagId);
        if (listTag) return listTag;

        for (const { kpi } of allKpiContextsIncludingInactive) {
            if (kpi.submission?.report_tag?.report_tag_id === reportTagId) {
                return {
                    report_tag_id: kpi.submission.report_tag.report_tag_id,
                    name: kpi.submission.report_tag.report_tag_name,
                    since: kpi.submission.report_tag.report_tag_since ?? null,
                    until: kpi.submission.report_tag.report_tag_until ?? null,
                    deadline: null,
                    report_tag_status_name:
                        kpi.submission.report_tag.report_tag_status_name ?? null,
                };
            }
        }

        return null;
    }, [allKpiContextsIncludingInactive, tags, reportTagId]);

    const tagStatusName =
        (selectedTag as { report_tag_status_name?: string | null } | null)
            ?.report_tag_status_name ?? null;
    const tagIsOpen =
        tagStatusName == null || tagStatusName === ""
            ? true
            : isTagOpen(tagStatusName);
    const tagStatusLabel = getReportTagStatusLabel(tagStatusName);
    const periodBlocksWrites = !tagIsOpen;

    const activityTemplateMeta = data?.[0]?.report_template ?? null;

    const fallbackActivityStatusName = useMemo(() => {
        for (const { kpi } of allKpiContextsIncludingInactive) {
            if (kpi.submission?.activity_entry_status_name) {
                return kpi.submission.activity_entry_status_name;
            }
        }
        return null;
    }, [allKpiContextsIncludingInactive]);

    const activityStatusName =
        activityTemplateMeta?.activity_report_status_name ??
        activityTemplateMeta?.process_results?.log_status ??
        fallbackActivityStatusName;
    const parentDepartmentStatusName =
        parentDepartmentReport?.department_report_status_name ?? null;

    const hierarchyActionsLocked = activityTemplateMeta?.actions_locked === true;
    const activityEditable =
        canEditActivitySubmission(activityStatusName, parentDepartmentStatusName) &&
        !hierarchyActionsLocked;
    const fieldsEditable =
        activityEditable && (canSaveActivity || canSubmitActivity) && tagIsOpen;
    const showPrefillControls =
        fieldsEditable &&
        !activityStatusName &&
        Boolean(entityScopeType) &&
        entityId > 0 &&
        reportTemplateId > 0 &&
        reportTagId > 0;
    const showSubmitButton =
        activityEditable && canSubmitActivity && !permissionsLoading && tagIsOpen;
    const showSaveButton =
        activityEditable && canSaveActivity && !permissionsLoading && tagIsOpen;
    const activityStatusLabel = getWorkflowStatusLabel(activityStatusName);
    const activityStatusVariant = getWorkflowStatusVariant(activityStatusName);
    const normalizedActivityStatus = normalizeReportWorkflowStatus(activityStatusName);
    const parentDepartmentStatusLabel = getWorkflowStatusLabel(
        parentDepartmentStatusName
    );
    const parentDepartmentStatusVariant = getWorkflowStatusVariant(
        parentDepartmentStatusName
    );
    const parentDepartmentStatusDisplayLabel = parentDepartmentStatusName
        ? parentDepartmentStatusLabel
        : parentDepartmentReportLoading
            ? "Loading…"
            : getWorkflowStatusLabel("NOT_STARTED");
    const periodRangeText =
        selectedTag?.since || selectedTag?.until
            ? `${selectedTag?.since ?? "—"} to ${selectedTag?.until ?? "—"}`
            : "—";
    const needsAttentionReason = useMemo(() => {
        if (normalizedActivityStatus !== "NEEDS_ATTENTION") return null;

        const details = activityTemplateMeta?.process_results?.details?.trim();
        const message = activityTemplateMeta?.process_results?.message?.trim();

        if (details) return details;
        if (message) return message;
        return null;
    }, [
        activityTemplateMeta?.process_results?.details,
        activityTemplateMeta?.process_results?.message,
        normalizedActivityStatus,
    ]);

    const departmentReportPath = useMemo(() => {
        if (entityId <= 0 || activeDepartmentId <= 0 || reportTagId <= 0) return null;

        const params = buildReportViewSearch({
            departmentId: activeDepartmentId,
            reportTagId,
        });

        if (entityScopeType === "district") {
            return `/district/${entityId}/reports/view?${params}`;
        }
        if (entityScopeType === "nation") {
            return `/nation/${entityId}/reports/view?${params}`;
        }
        if (entityScopeType === "church") {
            return `/church/${entityId}/reports/view?${params}`;
        }
        return null;
    }, [entityId, entityScopeType, activeDepartmentId, reportTagId]);

    const canRequestReviewNow =
        !hierarchyActionsLocked &&
        canRequestActivityReview(activityStatusName, parentDepartmentStatusName) &&
        !!parentDepartmentReport?.department_report_id;
    const showRequestReviewButton =
        canRequestReviewNow && canReviewActivity && !permissionsLoading;

    const currentUserId = useAuthStore.getState().getAuthUser()?.id ?? null;
    const reviewCopy = getRequestReviewCopy(
        currentUserId,
        activityTemplateMeta?.submitted_by
    );
    const reviewButtonLabel = reviewCopy.actionLabel;

    const accessLabel = fieldsEditable ? "Editable" : "View only";
    const editabilityText = getActivityEditabilityText(
        fieldsEditable,
        activityStatusName,
        parentDepartmentStatusName,
        parentDepartmentStatusLabel
    );

    const loadParentDepartmentReport = useCallback(async () => {
        if (entityId <= 0 || activeDepartmentId <= 0 || reportTagId <= 0) {
            setParentDepartmentReport(null);
            setParentDepartmentReportLoading(false);
            return null;
        }

        setParentDepartmentReportLoading(true);
        try {
            let response;
            if (entityScopeType === "district") {
                response = await fetchDepartmentReportsQueryAsync({
                    scope: "district",
                    districtId: entityId,
                    departmentId: activeDepartmentId,
                    reportTagId,
                    page: 1,
                    perPage: 10,
                });
            } else if (entityScopeType === "nation") {
                response = await fetchDepartmentReportsQueryAsync({
                    scope: "nation",
                    nationId: entityId,
                    departmentId: activeDepartmentId,
                    reportTagId,
                    page: 1,
                    perPage: 10,
                });
            } else if (entityScopeType === "church") {
                response = await fetchDepartmentReportsQueryAsync({
                    scope: "church",
                    churchId: entityId,
                    departmentId: activeDepartmentId,
                    reportTagId,
                    page: 1,
                    perPage: 10,
                });
            } else {
                setParentDepartmentReport(null);
                return null;
            }

            if (!response.isSuccess()) {
                setParentDepartmentReport(null);
                return null;
            }

            const items = response.getData()?.data ?? [];
            const match =
                items.find(
                    (item) =>
                        item.department_id === activeDepartmentId &&
                        Number(item.report_tag_id ?? 0) === reportTagId
                ) ?? null;
            setParentDepartmentReport(match);
            return match;
        } finally {
            setParentDepartmentReportLoading(false);
        }
    }, [entityId, entityScopeType, activeDepartmentId, reportTagId]);

    useEffect(() => {
        void loadParentDepartmentReport();
    }, [loadParentDepartmentReport]);

    useEffect(() => {
        setShowDepartmentReportNotice(false);
    }, [entityId, reportTemplateId, reportTagId, activeDepartmentId]);

    useEffect(() => {
        if (!data?.length) return;

        setActiveSubGroupTabs((prev) => {
            const next = { ...prev };
            let changed = false;

            data.forEach((group, groupIdx) => {
                const subGroups = sortSubGroups(group.sub_groups);
                const usesSubGroups =
                    Boolean(group.has_subgroups) || subGroups.length > 0;

                if (!usesSubGroups || subGroups.length === 0) return;

                const groupKey = `${group.report_template.report_template_id}-${groupIdx}`;
                const validTabKeys = [
                    ...subGroups.map((subGroup, subGroupIdx) =>
                        subgroupTabKey(
                            subGroup.report_sub_group_id,
                            `${groupKey}-${subGroupIdx}`
                        )
                    ),
                    SUMMARY_TAB_EVENT_KEY,
                ];

                if (!next[groupKey] || !validTabKeys.includes(next[groupKey])) {
                    next[groupKey] = validTabKeys[0];
                    changed = true;
                }
            });

            return changed ? next : prev;
        });
    }, [data]);

    // open ALL activities by default once activities are available
    useEffect(() => {
        if (!allActivityContexts.length) return;

        const allIds = allActivityContexts.map(({ subGroupId, activity }) =>
            getActivityAccordionKey(subGroupId, activity.activity_id)
        );

        setOpenKeys(allIds);
    }, [allActivityContexts]);

    const toggleKey = (key: string) => {
        setOpenKeys(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]));
    };


    // Notify parent so page header can show template name + department
    useEffect(() => {
        if (!onHeaderContextChange) return;

        let next: ActivityEntryHeaderContext | null = null;
        if (data && data.length > 0) {
            const first = data[0];
            const tmpl = first?.report_template;
            if (tmpl) {
                next = {
                    templateName: tmpl.report_template_name ?? "",
                    departmentName: tmpl.department_name ?? "",
                    reportTagName: selectedTag?.name ?? null,
                    reportTagStatusLabel: tagStatusLabel || null,
                    reportTagSince: selectedTag?.since ?? null,
                    reportTagUntil: selectedTag?.until ?? null,
                    reportTagDeadline: selectedTag?.deadline ?? null,
                    tagIsOpen,
                    activityStatusName,
                    activityStatusLabel,
                    activityStatusVariant,
                    parentDepartmentStatusLabel: parentDepartmentStatusDisplayLabel,
                    parentDepartmentStatusVariant: parentDepartmentStatusName ? parentDepartmentStatusVariant : "secondary",
                    periodRangeText,
                    accessLabel,
                    editabilityText,
                    needsAttentionReason,
                };
            }
        }

        if (
            lastNotifiedHeaderRef.current !== undefined &&
            areActivityEntryHeaderContextsEqual(lastNotifiedHeaderRef.current, next)
        ) {
            return;
        }

        lastNotifiedHeaderRef.current = next;
        onHeaderContextChange(next);
    }, [
        data,
        onHeaderContextChange,
        selectedTag?.name,
        selectedTag?.since,
        selectedTag?.until,
        selectedTag?.deadline,
        tagStatusLabel,
        tagIsOpen,
        activityStatusName,
        activityStatusLabel,
        activityStatusVariant,
        parentDepartmentStatusDisplayLabel,
        parentDepartmentStatusName,
        parentDepartmentStatusVariant,
        periodRangeText,
        accessLabel,
        editabilityText,
        needsAttentionReason,
    ]);



    const isKpiSubmitted = (kpi: {
        submission?: {
            activity_entry_status_name?: string | null;
            activity_entry_status_id?: number | null;
        } | null;
    }) => {
        const normalizedStatus = normalizeReportWorkflowStatus(
            kpi.submission?.activity_entry_status_name
        );

        return (
            normalizedStatus === "SUBMITTED" ||
            normalizedStatus === "AWAITING_APPROVAL" ||
            normalizedStatus === "APPROVED" ||
            normalizedStatus === "FINAL"
        );
    };

    const areAllKpisSubmittedForActivity = useCallback((context: ActivityContext) => {
        const kpis = getActivityKpiContexts(context, showInactive);
        if (kpis.length === 0) return false; // no KPIs -> don't lock buttons by accident
        return kpis.every(({ kpi }) => isKpiSubmitted(kpi));
    }, [showInactive]);

    const allSubmitted = useMemo(() => {
        if (allKpiContexts.length === 0) return false;
        return allKpiContexts.every(({ kpi }) => isKpiSubmitted(kpi));
    }, [allKpiContexts]);

    const applyCalculatedWarnings = useCallback((response: { isSuccess: () => boolean; getData: () => { calculated_kpi_warnings?: Array<{ kpi_id: number; message: string }> } | null }) => {
        if (!response.isSuccess()) return;
        const warnings = response.getData()?.calculated_kpi_warnings ?? [];
        const next: Record<number, string> = {};
        for (const warning of warnings) {
            if (warning?.kpi_id != null && warning.message) {
                next[Number(warning.kpi_id)] = warning.message;
            }
        }
        setCalculatedWarningsByKpiId(next);
    }, []);

    const {
        buildDraftPayloadForActivity,
        buildDraftPayloadForAll,
        handleSaveDraft,
        saveAllDrafts,
        syncBusy,
    } = useActivityEntryDrafting({
        enabled,
        activityEditable: fieldsEditable,
        busy: busy || isSubmitting,
        tagIsClosed: periodBlocksWrites,
        autoSaveEnabled,
        entityId,
        reportTagId,
        showInactive,
        hydrationKey: `${entityScopeType}-${entityId}-${reportTemplateId}-${reportTagId}`,
        values,
        allKpiContexts,
        unsavedCount,
        markSaved,
        applyServerValues,
        doSaveDraft,
        reload,
        loadParentDepartmentReport,
        showSubmitConfirm,
        showReviewModal,
        gridModalOpen,
    });

    const formBusy = busy || isSubmitting || syncBusy;

    const handleSaveDraftWithWarnings = useCallback(
        async (...args: Parameters<typeof handleSaveDraft>) => {
            const response = await handleSaveDraft(...args);
            if (response) applyCalculatedWarnings(response as any);
            return response;
        },
        [applyCalculatedWarnings, handleSaveDraft]
    );

    const saveAllDraftsWithWarnings = useCallback(
        async (mode: "manual" | "hotkey" | "auto") => {
            const response = await saveAllDrafts(mode);
            if (response) applyCalculatedWarnings(response as any);
            return response;
        },
        [applyCalculatedWarnings, saveAllDrafts]
    );

    const handleOpenDepartmentReport = useCallback(() => {
        if (!departmentReportPath) return;
        navigate(departmentReportPath);
    }, [departmentReportPath, navigate]);

    // Find required KPIs that are missing values
    const missingRequired = useMemo(() => {
        const missing: { activityName: string; kpiName: string; subGroupName?: string | null }[] = [];
        const tagSince = selectedTag?.since ?? null;
        const tagUntil = selectedTag?.until ?? null;

        for (const { subGroupName, activity, kpi, subGroupId } of allKpiContexts) {
            if (!isKpiRequiredForReportTag(kpi.kpi_is_required, kpi.kpi_required_at, tagSince, tagUntil)) {
                continue;
            }

            const key = getKpiValueKey(subGroupId, activity.activity_id, kpi.kpi_id);
            if (!isFilled(values[key])) {
                missing.push({
                    activityName: activity.activity_name,
                    kpiName: kpi.kpi_name,
                    subGroupName,
                });
            }
        }

        return missing;
    }, [allKpiContexts, selectedTag, values]);


    const confirmSubmit = async () => {
        if (missingRequired.length > 0) {
            showValidation(
                missingRequired.map((m) =>
                    m.subGroupName
                        ? `Required: ${m.kpiName} in ${m.activityName} (${m.subGroupName})`
                        : `Required: ${m.kpiName} in ${m.activityName}`
                )
            );
            return;
        }

        setIsSubmitting(true);
        try {
            if (unsavedCount > 0) {
                const draftPayload = buildDraftPayloadForAll();
                const saveResponse = await doSaveDraft(entityId, draftPayload, { silent: true });
                if (!saveResponse.isSuccess()) {
                    return;
                }
                applyCalculatedWarnings(saveResponse);
                markSaved(draftPayload);
            }

            const submitResponse = await doSubmit(entityId, {
                report_template_id: reportTemplateId,
                report_tag_id: reportTagId,
            });
            if (submitResponse.isSuccess()) {
                applyCalculatedWarnings(submitResponse);
                setShowDepartmentReportNotice(true);
                const groups = await reload();
                if (groups) {
                    applyServerValues(
                        buildFormValuesFromGroups(groups, showInactive),
                        collectCalculatedValueKeys(groups, showInactive)
                    );
                }
                await loadParentDepartmentReport();
            }
        } catch (e) {
            console.error(e);
        } finally {
            setIsSubmitting(false);
            setShowSubmitConfirm(false);
        }
    };

    const openReviewModal = () => {
        setReviewReason("");
        setReviewTouched(false);
        setReviewError(null);
        setShowReviewModal(true);
    };

    const closeReviewModal = () => {
        if (formBusy) return;
        setShowReviewModal(false);
    };

    const handleRequestReview = async () => {
        setReviewTouched(true);
        setReviewError(null);

        const messages: string[] = [];
        if (!parentDepartmentReport?.department_report_id) {
            messages.push("A linked department report is required before review can be requested.");
        }
        if (!reviewReasonValid) {
            messages.push("Please provide a reason for review between 10 and 1000 characters.");
        }
        if (messages.length > 0) {
            showValidation(messages);
            return;
        }

        try {
            let response;
            if (scope?.type === "district") {
                response = await requestDistrictActivityEntryReviewAsync(
                    scope.id,
                    reportTemplateId,
                    reportTagId,
                    { review_notes: reviewReasonTrimmed }
                );
            } else if (scope?.type === "nation") {
                response = await requestNationActivityEntryReviewAsync(
                    scope.id,
                    reportTemplateId,
                    reportTagId,
                    { review_notes: reviewReasonTrimmed }
                );
            } else if (scope?.type === "church") {
                response = await requestActivityEntryReviewAsync(
                    churchId,
                    reportTemplateId,
                    reportTagId,
                    { review_notes: reviewReasonTrimmed }
                );
            } else {
                return;
            }

            if (!response.isSuccess()) {
                showError(response.getError() as ErrorResponse);
                setReviewError(
                    response.getError()?.detail ??
                        "Failed to request review for this activity submission."
                );
                return;
            }

            showSuccess(
                response.getData()?.message ??
                    "Review requested successfully. The item is now ready for correction."
            );
            setShowReviewModal(false);
            await reload();
            await loadParentDepartmentReport();
        } catch (error) {
            showError(error as ErrorResponse);
            setReviewError(
                error instanceof Error
                    ? error.message
                    : "Failed to request review for this activity submission."
            );
        }
    };

    const renderActivityAccordion = (contexts: ActivityContext[]) => (
        <ActivityFormAccordion
            contexts={contexts}
            openKeys={openKeys}
            onToggleKey={toggleKey}
            showInactive={showInactive}
            values={values}
            activityEditable={fieldsEditable}
            showSaveButton={showSaveButton}
            busy={formBusy}
            tagIsClosed={periodBlocksWrites}
            reportTagSince={selectedTag?.since ?? null}
            reportTagUntil={selectedTag?.until ?? null}
            isActivityDirty={isActivityDirty}
            areAllKpisSubmittedForActivity={areAllKpisSubmittedForActivity}
            isKpiSubmitted={isKpiSubmitted}
            onChange={handleChange}
            onSaveActivity={(ctx, override) => {
                const payload = buildDraftPayloadForActivity(ctx);
                if (override) {
                    payload.entries = payload.entries.map((entry) =>
                        entry.kpi_id === override.kpiId
                            ? { ...entry, kpi_value: override.kpiValue.toString().trim() }
                            : entry
                    );
                }
                return handleSaveDraftWithWarnings(payload);
            }}
            onGridModalOpenChange={setGridModalOpen}
            calculatedWarningsByKpiId={calculatedWarningsByKpiId}
        />
    );

    if (!enabled) {
        return (
            <ActivityFormSelector
                entityScope={entityScopeType}
                entityId={entityId}
                selector={selector}
                setSelector={setSelector}
                departments={departments}
                departmentsLoading={departmentsLoading}
                templates={templates}
                templatesLoading={templatesLoading}
                tags={openTagsForSelector}
                tagsLoading={tagsLoading}
                onOpen={() => {
                    const params = buildActivityEntrySubmitSearch({
                        reportTemplateId: selector.report_template_id,
                        reportTagId: selector.report_tag_id,
                        departmentId: selector.department_id,
                    });
                    navigate(`${basePath}?${params.toString()}`);
                }}
            />
        );
    }

    if ((loading || previewLoading) && !isSubmitting) {
        return (
            <div className="activity-entry-form__wrap">
                <div className="d-flex justify-content-center align-items-center gap-2 py-4">
                    <Spinner animation="border" variant="primary" />
                    <span className="text-muted">Loading form…</span>
                </div>
            </div>
        );
    }

    if (requestFailed) {
        return (
            <div className="activity-entry-form__wrap">
                <div className="ntcog-form-section">
                    <Alert variant="danger" className="mb-3">
                        Failed to load this activity form from the server.
                    </Alert>

                    <NoResult
                        line_1="The activity form could not be loaded."
                        line_2="Please try again or change the selection."
                    />

                    <div className="d-flex justify-content-center mt-3">
                        <Button
                            variant="outline-secondary"
                            size="lg"
                            className="btn-action"
                            onClick={() => {
                                navigate(basePath);
                            }}
                        >
                            Change Selection
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    if (!data || data.length === 0) {
        return (
            <div className="activity-entry-form__wrap">
                <div className="ntcog-form-section">
                    <NoResult
                        line_1="No activity form is available for this selection."
                        line_2="This template may not have any activities or KPIs assigned yet."
                    />

                    <div className="d-flex justify-content-center mt-3">
                        <Button
                            variant="outline-secondary"
                            size="lg"
                            className="btn-action"
                            onClick={() => {
                                // remove parameters -> form shows again
                                navigate(basePath);
                            }}
                        >
                            Change Selection
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="activity-entry-form__wrap">
            <Form
                onSubmit={(e) => e.preventDefault()}
                className="d-flex flex-column gap-3"
            >
                {showDepartmentReportNotice && departmentReportPath ? (
                    <Alert variant="success" className="mb-0">
                        <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
                            <div>
                                <p className="mb-0">
                                    The department report for this reporting period is now available.
                                </p>
                            </div>
                            <Button
                                variant="outline-success"
                                className="btn-action"
                                onClick={handleOpenDepartmentReport}
                            >
                                Open
                            </Button>
                        </div>
                    </Alert>
                ) : null}

                {periodBlocksWrites ? (
                    <Alert variant="warning" className="py-2 mb-0 small">
                        This reporting period is not open. Saving and submitting are unavailable.
                    </Alert>
                ) : null}

                {showPrefillControls && entityScopeType ? (
                    <div className="d-flex justify-content-end">
                        <ActivityEntryPrefillControls
                            scope={entityScopeType}
                            entityId={entityId}
                            reportTemplateId={reportTemplateId}
                            reportTagId={reportTagId}
                            targetSince={selectedTag?.since ?? null}
                            kpiContexts={allKpiContexts}
                            disabled={formBusy}
                            onApplyValues={applyPrefillValues}
                        />
                    </div>
                ) : null}

            {entryData.length === 0 ? (
                <Alert variant="info" className="mb-0">
                    No saved activity data exists for this selection yet. Enter values below to start the submission.
                </Alert>
            ) : null}

            {data.map((group: ActivityEntryFormGroup, groupIdx: number) => {
                const tmpl = group.report_template;
                const groupKey = `${tmpl.report_template_id}-${groupIdx}`;
                const subGroups = sortSubGroups(group.sub_groups);
                const hasSubGroups =
                    Boolean(group.has_subgroups) || subGroups.length > 0;
                const groupActivityContexts = getGroupActivityContexts(group, showInactive);
                const subGroupTabs = hasSubGroups
                    ? subGroups.map((subGroup, subGroupIdx) => {
                        const eventKey = subgroupTabKey(
                            subGroup.report_sub_group_id,
                            `${groupKey}-${subGroupIdx}`
                        );

                        return {
                            eventKey,
                            title: subGroup.sub_group_name ?? `Sub Group ${subGroupIdx + 1}`,
                            contexts: groupActivityContexts.filter(
                                (context) =>
                                    context.subGroupId === (subGroup.report_sub_group_id ?? null) &&
                                    (context.subGroupName ?? null) ===
                                        (subGroup.sub_group_name ?? null)
                            ),
                        };
                    })
                    : [];
                const activeTabKey = subGroupTabs.length > 0
                    ? activeSubGroupTabs[groupKey] ?? subGroupTabs[0].eventKey
                    : null;

                return (
                    <div key={`${tmpl.report_template_id}-${groupIdx}`}>
                        <div className="ntcog-form-section">
                                {hasSubGroups ? (
                                    subGroupTabs.length > 0 ? (
                                        <Tabs
                                            activeKey={activeTabKey ?? undefined}
                                            onSelect={(eventKey) => {
                                                if (!eventKey) return;
                                                setActiveSubGroupTabs((prev) => ({
                                                    ...prev,
                                                    [groupKey]: eventKey,
                                                }));
                                            }}
                                            className="mb-3"
                                        >
                                            {subGroupTabs.map((tabConfig) => (
                                                <Tab
                                                    eventKey={tabConfig.eventKey}
                                                    title={tabConfig.title}
                                                >
                                                    <div className="pt-3">
                                                        {renderActivityAccordion(tabConfig.contexts)}
                                                    </div>
                                                </Tab>
                                            ))}
                                            <Tab
                                                eventKey={SUMMARY_TAB_EVENT_KEY}
                                                title={SUMMARY_TAB_LABEL}
                                            >
                                                <ActivityEntrySubgroupSummary
                                                    group={group}
                                                    unsavedCount={unsavedCount}
                                                />
                                            </Tab>
                                        </Tabs>
                                    ) : (
                                        <div className="p-3 p-md-4 text-muted small">
                                            No sub groups assigned to this template.
                                        </div>
                                    )
                                ) : (
                                    renderActivityAccordion(groupActivityContexts)
                                )}
                            </div>

                    </div>

                );
            })}

            {showSubmitButton ? (
                <div className="p-3 p-md-4">
                    <div className="d-flex justify-content-end">
                        <Button
                            variant="primary"
                            size="lg"
                            className="btn-action"
                            onClick={() => setShowSubmitConfirm(true)}
                            disabled={formBusy || allSubmitted}
                            aria-busy={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" aria-hidden />
                                    Submitting…
                                </>
                            ) : (
                                "Submit"
                            )}
                        </Button>
                    </div>
                </div>
            ) : null}

            {showRequestReviewButton ? (
                <div className="d-flex justify-content-end flex-wrap gap-2">
                    <Button
                        variant="primary"
                        className="btn-action"
                        onClick={openReviewModal}
                        disabled={formBusy}
                    >
                        {reviewButtonLabel}
                    </Button>
                </div>
            ) : null}

            {showSaveButton ? (
                <button
                    type="button"
                    className="activity-entry-save-fab"
                    onClick={() => {
                        void saveAllDraftsWithWarnings("manual");
                    }}
                    disabled={formBusy || unsavedCount === 0}
                    aria-busy={busy}
                    title={
                        autoSaveEnabled
                            ? "Save draft now (Ctrl+S). Auto-save is enabled."
                            : "Save draft now (Ctrl+S)."
                    }
                >
                    <FiSave aria-hidden />
                    <span>{busy ? "Saving..." : unsavedCount > 0 ? "Save Draft" : "All Saved"}</span>
                    {unsavedCount > 0 ? (
                        <span className="activity-entry-save-fab__count">
                            {unsavedCount}
                        </span>
                    ) : null}
                </button>
            ) : null}

            <Modal
                show={showReviewModal}
                onHide={closeReviewModal}
                centered
                backdrop="static"
                size="lg"
            >
                <Modal.Header closeButton={!busy} className="border-0 pb-0">
                    <Modal.Title className="fw-semibold">{reviewButtonLabel}</Modal.Title>
                </Modal.Header>

                <Modal.Body className="pt-3">
                    <p className="text-muted mb-3">
                        Request {reviewCopy.noun} to move this activity submission into
                        <span className="fw-semibold"> {getWorkflowStatusLabel("NEEDS_ATTENTION")}</span> so it can be edited and submitted again.
                    </p>

                    <Form.Group className="mb-0">
                        <Form.Label className="fw-medium">
                            {reviewCopy.reasonLabel} <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={4}
                            value={reviewReason}
                            onChange={(e) => {
                                setReviewReason(e.target.value);
                                if (reviewTouched) setReviewError(null);
                            }}
                            onBlur={() => setReviewTouched(true)}
                            placeholder="Explain what needs to be corrected..."
                            disabled={busy}
                            isInvalid={reviewTouched && !reviewReasonValid}
                            maxLength={1000}
                        />
                        <div className="d-flex justify-content-between mt-1">
                            <Form.Text muted>10–1000 characters.</Form.Text>
                            <Form.Text muted>{reviewReason.length} / 1000</Form.Text>
                        </div>
                        <Form.Control.Feedback type="invalid">
                            Please provide a reason between 10 and 1000 characters.
                        </Form.Control.Feedback>
                    </Form.Group>

                    {reviewError ? (
                        <Alert variant="danger" className="mt-3 mb-0">
                            {reviewError}
                        </Alert>
                    ) : null}
                </Modal.Body>

                <Modal.Footer>
                    <Button
                        variant="outline-secondary"
                        onClick={closeReviewModal}
                        disabled={busy}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleRequestReview}
                        disabled={busy}
                        aria-busy={busy}
                    >
                        {busy ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" aria-hidden />
                                Requesting…
                            </>
                        ) : (
                            reviewCopy.confirmLabel
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
            </Form>

            <Modal
                show={showSubmitConfirm || isSubmitting}
                onHide={() => {
                    if (isSubmitting) return;
                    setShowSubmitConfirm(false);
                }}
                centered
                backdrop={isSubmitting ? "static" : true}
                keyboard={!isSubmitting}
                size="lg"
            >
                <Modal.Header closeButton={!isSubmitting} className="border-0 pb-0">
                    <Modal.Title className="fw-semibold">Submit Report Template Entry?</Modal.Title>
                </Modal.Header>

                <Modal.Body className="pt-3">
                    <div className="mb-2">
                        This will submit <strong>all activity entries</strong> for this reporting period for review.
                    </div>

                    {missingRequired.length > 0 && (
                        <Alert variant="danger" className="mb-2">
                            <div className="fw-semibold mb-1">Required KPIs missing values:</div>
                            <ul className="mb-0 ps-3">
                                {missingRequired.map((m, i) => (
                                    <li key={i}>
                                        <strong>{m.kpiName}</strong> in <em>{m.activityName}</em>
                                        {m.subGroupName ? ` (${m.subGroupName})` : ""}
                                    </li>
                                ))}
                            </ul>
                        </Alert>
                    )}

                    {Object.keys(calculatedWarningsByKpiId).length > 0 ? (
                        <Alert variant="warning" className="mb-2" data-testid="calculated-kpi-warnings">
                            <div className="fw-semibold mb-1">Calculated KPI notes:</div>
                            <ul className="mb-0 ps-3">
                                {Object.entries(calculatedWarningsByKpiId).map(([kpiId, message]) => (
                                    <li key={kpiId}>{message}</li>
                                ))}
                            </ul>
                        </Alert>
                    ) : null}

                    {unsavedCount > 0 && missingRequired.length === 0 ? (
                        <div className="alert alert-info mb-0">
                            <strong>{unsavedCount}</strong> {unsavedCount === 1 ? "activity has" : "activities have"} unsaved changes — these will be saved automatically when you submit.
                        </div>
                    ) : missingRequired.length === 0 ? (
                        <div className="text-muted small mb-0">
                            All changes are saved. Ready to submit.
                        </div>
                    ) : null}

                    <div className="d-flex justify-content-end gap-2 mt-3">
                        <Button
                            variant="primary"
                            size="lg"
                            className="btn-action"
                            onClick={confirmSubmit}
                            disabled={formBusy || allSubmitted || periodBlocksWrites}
                            aria-busy={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" aria-hidden />
                                    Submitting…
                                </>
                            ) : (
                                "Yes, Submit"
                            )}
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
}

export { ActivityEntryForm };
export default ActivityEntryForm;
